import { type ReactNode, useState } from "react";

import { PrimaryButton, SecondaryButton } from "@shared/buttons";

export const useWizard = (initialIndex = 0) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const goToNext = () => setCurrentIndex((prevIndex) => prevIndex + 1);
  const goToPrevious = () => setCurrentIndex((prevIndex) => prevIndex - 1);
  const setStep = (index: number) => setCurrentIndex(index);

  return {
    currentIndex,
    goToNext,
    goToPrevious,
    setStep,
  };
};

type Props = {
  steps: {
    children: ReactNode;
    onPrevious?: ({ goToPrevious }: { goToPrevious: () => void }) => void;
    onNext?: ({ goToNext }: { goToNext: () => void }) => void;
    nextStepText?: string;
    previousStepText?: string;
    customFooter?: ({
      goToNext,
      goToPrevious,
    }: {
      goToNext: () => void;
      goToPrevious: () => void;
    }) => ReactNode;
  }[];
  submitText?: string;
  onSubmit?: () => void;
  className?: string;
  currentIndex: number;
  goToNext: () => void;
  goToPrevious: () => void;
  setStep: (index: number) => void;
  shouldHideDefaultFooter?: boolean;
};

export const Wizard = ({
  steps,
  onSubmit,
  submitText,
  className,
  currentIndex,
  goToNext,
  goToPrevious,
  shouldHideDefaultFooter,
}: Props) => {
  return (
    <div className={className}>
      {steps.map(
        (
          {
            children,
            nextStepText,
            previousStepText,
            customFooter,
            onNext,
            onPrevious,
          },
          index,
        ) => (
          <div
            key={index}
            style={{
              display: currentIndex === index ? "block" : "none",
            }}
          >
            {children}
            {(!shouldHideDefaultFooter &&
              customFooter?.({ goToNext, goToPrevious })) ?? (
              <div className="mt-4 space-x-2 flex justify-end">
                {currentIndex > 0 && (
                  <SecondaryButton
                    onClick={() => {
                      if (onPrevious) {
                        onPrevious({ goToPrevious });
                      } else {
                        goToPrevious();
                      }
                    }}
                  >
                    {previousStepText ?? "Back"}
                  </SecondaryButton>
                )}
                {currentIndex < steps.length - 1 && (
                  <PrimaryButton
                    onClick={() => {
                      if (onNext) {
                        onNext({ goToNext });
                      } else {
                        goToNext();
                      }
                    }}
                  >
                    {nextStepText ?? "Next"}
                  </PrimaryButton>
                )}
                {currentIndex === steps.length - 1 && (
                  <PrimaryButton onClick={() => onSubmit?.()}>
                    {submitText ?? "Submit"}
                  </PrimaryButton>
                )}
              </div>
            )}
          </div>
        ),
      )}
    </div>
  );
};
